import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Thumbs, FreeMode } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/thumbs';
import 'swiper/css/free-mode';
import {
    Bath,
    BedDouble,
    Coffee,
    Lamp,
    Phone,
    ShieldCheck,
    Sparkles,
    SunMedium,
    Trees,
    UtensilsCrossed,
    Waves,
    Wifi,
    Wind,
    Droplets
} from 'lucide-react';

import heroImg from '../assets/Resort/62.jpeg';
import galleryOne from '../assets/Resort/58.jpeg';
import galleryTwo from '../assets/Resort/64.jpeg';
import galleryThree from '../assets/Resort/72.jpeg';
import galleryFour from '../assets/Resort/70.jpeg';   // ← add these
import galleryFive from '../assets/Resort/68.jpeg';
import gallery2 from '../assets/Resort/74.jpeg';    // ← new import

const getHighlights = (t) => [
    {
        titleKey: 'suiteRoom.highlight1Title',
        descriptionKey: 'suiteRoom.highlight1Desc',
        icon: Waves
    },
    {
        titleKey: 'suiteRoom.highlight2Title',
        descriptionKey: 'suiteRoom.highlight2Desc',
        icon: Lamp
    },
    {
        titleKey: 'suiteRoom.highlight3Title',
        descriptionKey: 'suiteRoom.highlight3Desc',
        icon: BedDouble
    }
];

const getInclusions = (t) => [
    t('suiteRoom.inclusion1'),
    t('suiteRoom.inclusion2'),
    t('suiteRoom.inclusion3'),
    t('suiteRoom.inclusion4'),
    t('suiteRoom.inclusion5'),
    t('suiteRoom.inclusion6')
];

const getAmenityGroups = (t) => [
    {
        title: 'Room Includes',
        items: [
            'Free Wifi',
            'Air Conditioning',
            'Hot water',
            'Mini fridge',
            'Safety deposit box',
            'Electric kettle',
            'Daily housekeeping',
            'Hair dryer'
        ]
    }
];

const getStats = (t) => [
    { labelKey: 'suiteRoom.statSize', valueKey: 'suiteRoom.statSizeValue', icon: Sparkles },
    { labelKey: 'suiteRoom.statView', valueKey: 'suiteRoom.statViewValue', icon: SunMedium },
    { labelKey: 'suiteRoom.statGuests', valueKey: 'suiteRoom.statGuestsValue', icon: Trees },
    { labelKey: 'suiteRoom.statBed', valueKey: 'suiteRoom.statBedValue', icon: BedDouble }
];

const galleryImages = [
    galleryOne,
    galleryTwo,
    galleryThree,
    galleryFour,
    galleryFive,
];

export default function SuiteRoom() {
    const { t } = useTranslation('rooms');
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const highlights = getHighlights(t);
    const inclusions = getInclusions(t);
    const amenityGroups = getAmenityGroups(t);
    const stats = getStats(t);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const navigationButtons = [
        { label: t('suiteRoom.navAll'), path: '/beach/rooms' },
        { label: t('suiteRoom.navDeluxe'), path: '/beach/rooms/deluxe' },
        { label: t('suiteRoom.navDeluxeSeaView'), path: '/beach/rooms/deluxe-sea-view' },
        { label: t('suiteRoom.navSuite'), path: '/beach/rooms/luxury-suite' }
    ];
    const isActive = (path) => location.pathname === path;
    const location = useLocation();

    return (
        <motion.div
            className="bg-white text-gray-900"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
        >
            <section className="relative h-[75vh] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${heroImg})` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/20" />
                </div>

                <div className="relative h-full container px-6 flex flex-col justify-end pb-16">
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-sm uppercase tracking-[0.3em] text-amber-200 mb-4"
                    >
                        {t('suiteRoom.resortName')}
                    </motion.p>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-4xl md:text-5xl lg:text-6xl font-light text-white leading-tight max-w-3xl"
                    >
                        {t('suiteRoom.heroTitle')}
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="mt-6 text-lg md:text-xl text-green-50 max-w-2xl"
                    >
                        {t('suiteRoom.heroSubtitle')}
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.35 }}
                        className="mt-8 flex flex-wrap gap-4"
                    >
                        <Link
                            to="/beach/search"
                            className="px-7 py-3 rounded-full bg-amber-500 text-white font-semibold shadow-lg hover:bg-amber-600 transition-colors inline-block"
                        >
                            {t('suiteRoom.bookThisRoom')}
                        </Link>
                        <a
                            href="https://wa.me/94777382466"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-7 py-3 rounded-full border border-white text-white font-semibold hover:bg-white/10 transition-colors inline-block"
                        >
                            {t('suiteRoom.callReservations')}
                        </a>
                    </motion.div>
                </div>
            </section>

            <section className="sticky top-0 bg-white/95 backdrop-blur-sm shadow-sm z-10 border-b border-green-100">
                <div className="container px-6 py-4">
                    <div className="flex items-center justify-center md:justify-start">
                        <div className="flex space-x-3 overflow-x-auto">
                            {navigationButtons.map((btn) => (
                                <Link
                                    key={btn.path}
                                    to={btn.path}
                                    className={`px-6 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${isActive(btn.path)
                                        ? 'bg-green-700 text-white'
                                        : 'text-gray-600 hover:bg-green-100 hover:text-green-800'
                                        }`}
                                >
                                    {btn.label}
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="px-6 py-16 space-y-10 bg-[#1F1F1F]">
                <div className="container grid lg:grid-cols-3 gap-10 items-start  ">
                    <div className="lg:col-span-2 space-y-6">
                        <h2 className="text-3xl md:text-4xl font-light text-white">{t('suiteRoom.sectionTitle')}</h2>
                        <p className="text-lg text-white leading-relaxed">
                            {t('suiteRoom.sectionDesc')}
                        </p>
                        <div className="grid sm:grid-cols-2 gap-4">
                            {highlights.map((item) => (
                                <div key={item.titleKey} className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50">
                                    <item.icon className="w-6 h-6 text-emerald-700" />
                                    <div>
                                        <p className="font-semibold text-gray-900">{t(item.titleKey)}</p>
                                        <p className="text-sm text-gray-700 leading-relaxed">{t(item.descriptionKey)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="bg-gray-900 text-white rounded-2xl p-8 space-y-6">
                        <p className="text-sm uppercase tracking-[0.2em] text-amber-300">{t('suiteRoom.atAGlance')}</p>
                        <div className="grid grid-cols-2 gap-4">
                            {stats.map((stat) => (
                                <div key={stat.labelKey} className="p-4 rounded-xl bg-white/5 border border-white/10">
                                    <stat.icon className="w-6 h-6 text-amber-300 mb-2" />
                                    <p className="text-sm text-white/70">{t(stat.labelKey)}</p>
                                    <p className="text-lg font-semibold">{t(stat.valueKey)}</p>
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center justify-between">

                            <Link
                                to="/beach/search"
                                className="px-5 py-3 rounded-full bg-amber-500 text-gray-900 font-semibold hover:bg-amber-400 transition-colors inline-block"
                            >
                                {t('suiteRoom.reserve')}
                            </Link>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto">
                    {/* Main Swiper - big images */}
                    <Swiper
                        modules={[Navigation, Pagination, Thumbs, FreeMode]}
                        spaceBetween={12}
                        navigation
                        pagination={{ clickable: true }}
                        thumbs={{ swiper: thumbsSwiper }}           // connect to thumbs
                        className="rounded-2xl overflow-hidden mb-4 main-swiper"
                        loop={true}
                    >
                        {galleryImages.map((img, index) => (
                            <SwiperSlide key={index}>
                                <div className="relative aspect-[4/3] md:aspect-[16/9] overflow-hidden">
                                    <img
                                        src={img}
                                        alt={t('suiteRoom.galleryAlt')}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    {/* Thumbnail Swiper - smaller preview */}
                    <Swiper
                        onSwiper={setThumbsSwiper}                    // important connection
                        modules={[FreeMode, Thumbs]}
                        spaceBetween={10}
                        slidesPerView={4}
                        freeMode={true}
                        watchSlidesProgress={true}
                        className="thumbs-swiper"
                        breakpoints={{
                            640: { slidesPerView: 5 },
                            1024: { slidesPerView: 6 },
                        }}
                    >
                        {galleryImages.map((img, index) => (
                            <SwiperSlide key={index}>

                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </section>

            <section className="bg-gray-50 py-16">
                <div className="max-w-6xl mx-auto px-6 space-y-10">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <p className="text-sm uppercase tracking-[0.2em] text-emerald-700">{t('suiteRoom.inclusionsTitle')}</p>
                            <h3 className="text-3xl font-light text-gray-900">{t('suiteRoom.inclusionsSubtitle')}</h3>
                        </div>
                        <div className="flex items-center gap-3 text-emerald-700">
                            <ShieldCheck className="w-5 h-5" />
                            <span className="text-sm">{t('suiteRoom.enhancedCleanliness')}</span>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="p-6 rounded-2xl bg-white shadow-sm space-y-3">
                            {inclusions.map((item) => (
                                <div key={item} className="flex items-center gap-3">
                                    <Sparkles className="w-5 h-5 text-amber-500" />
                                    <p className="text-gray-800">{item}</p>
                                </div>
                            ))}
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                            {amenityGroups.map((group, gi) => (
                                <div key={gi} className="p-5 rounded-2xl bg-white shadow-sm">
                                    <p className="font-semibold text-gray-900 mb-3">{group.title}</p>
                                    <ul className="space-y-2 text-sm text-gray-700 list-disc list-inside">
                                        {group.items.map((amenity, i) => (
                                            <li key={i}>{amenity}</li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-10 items-center">
                <div className="space-y-5">
                    <p className="text-sm uppercase tracking-[0.2em] text-emerald-700">{t('suiteRoom.essentialsTitle')}</p>
                    <h3 className="text-3xl font-light text-gray-900">{t('suiteRoom.essentialsHeading')}</h3>
                    <p className="text-lg text-gray-700 leading-relaxed">
                        {t('suiteRoom.essentialsDesc')}
                    </p>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {[
                            { icon: Wifi, label: 'Free Wifi' },
                            { icon: Wind, label: 'Air Conditioning' },
                            { icon: Droplets, label: 'Hot water' },
                            { icon: Coffee, label: 'Mini fridge' },
                            { icon: ShieldCheck, label: 'Safety deposit box' },
                            { icon: UtensilsCrossed, label: 'Electric kettle' },
                            { icon: Sparkles, label: 'Daily housekeeping' },
                            { icon: Bath, label: 'Hair dryer' }
                        ].map((item, idx) => (
                            <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50">
                                <item.icon className="w-5 h-5 text-emerald-700" />
                                <p className="text-sm font-medium text-gray-900">{item.label}</p>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="relative h-80 md:h-96 rounded-3xl overflow-hidden">
                    <img src={gallery2} alt={t('suiteRoom.oceanAlt')} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                    <div className="absolute bottom-6 left-6 text-white">
                        <p className="text-sm uppercase tracking-[0.2em] text-amber-200">{t('suiteRoom.oceanTitle')}</p>
                        <p className="text-2xl font-semibold">{t('suiteRoom.oceanSubtitle')}</p>
                    </div>
                </div>
            </section>

            <section className="bg-gray-900 text-white py-14">
                <div className="max-w-6xl mx-auto px-6 flex flex-col lg:flex-row items-center justify-between gap-6">
                    <div>
                        <p className="text-sm uppercase tracking-[0.2em] text-amber-300">{t('suiteRoom.ctaTitle')}</p>
                        <h4 className="text-3xl font-light">{t('suiteRoom.ctaHeading')}</h4>
                        <p className="text-white/70 mt-2">{t('suiteRoom.ctaDesc')}</p>
                    </div>
                    <div className="flex flex-wrap gap-4">
                        <Link
                            to="/beach/search"
                            className="px-6 py-3 rounded-full bg-amber-500 text-gray-900 font-semibold hover:bg-amber-400 transition-colors"
                        >
                            {t('suiteRoom.checkAvailability')}
                        </Link>
                        <a
                            href="https://wa.me/94777382466"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-6 py-3 rounded-full border border-white text-white font-semibold hover:bg-white/10 transition-colors flex items-center gap-2"
                        >
                            <Phone className="w-5 h-5" />
                            +94 77 738 2466
                        </a>
                    </div>
                </div>
            </section>
        </motion.div>
    );
}
